/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
/* eslint no-console: ["error", { allow: ["warn", "error"] }] */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';

import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useRollbar, useRollbarPerson } from '@rollbar/react';

import isEmpty from 'lodash/isEmpty';
import Swal from 'sweetalert2/src/sweetalert2';
import Tab from 'react-bootstrap/Tab';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import LinearProgress from '@mui/material/LinearProgress';

import apiService from '@services/apiService';

import {
  formSubmitted,
  loadComboBoxOptions,
  setValidationResult,
} from '@redux/modules/formGeneration';

import { getCurrentAgent } from '@redux/modules/agent';

import {
  checkForAutoSave,
  loadAutoSave,
  removeAutoSave,
  saveAutoSave,
  updateAutoSave,
  loadTaxAutoPopSelected,
} from '@redux/modules/listing';

import { getUserName, getUserMlsId } from '@redux/selectors/UserSelector';

import {
  getSortedErrors,
  selectComboValueDict,
  selectFormDefinition,
  selectEditPageName,
  selectInitialValues,
  selectLookupNames,
  selectListingRule,
  selectListingType,
  selectVisiblePageStructure,
  selectAllFields,
  selectListingCode,
  getOrderedSections,
  selectErrorsPerSection,
  selectRequiredPerSection,
} from '@redux/selectors/FormSelector';

import {
  selectCurrentListing,
  selectEditListingHeaderInfo,
  selectListingStdStatus,
  selectListingStdStatusVerb,
  selectUploadedSupplements,
  selectListingSelectedDoors,
  selectListingSelectedHistory,
  selectListingSelectedOpenHouses,
  selectListingSelectedParcels,
  selectListingSelectedRooms,
  selectListingSelectedUnits,
  selectRepeatableSectionCount,
  selectSectionsPerRepeatableSection,
} from '@redux/selectors/ListingSelector';

import {
  selectListingFormData,
} from '@redux/selectors/FormDataSelector';

import {
  selectPhotoMetaCollection,
  selectPhotoQueueLength,
} from '@redux/selectors/PhotoSelector';

import { listingDict } from '@redux/constants/GeneralConstants';

import AgentLookupModal from '@components/form/formComponents/inputGroups/agentLookup/AgentLookupModal';
import AutoPopModal from '@components/taxAutoPop/AutoPopModal';
import AutoSave from '@components/form/formHelpers/AutoSave';
import Badge from '@components/smartAdminUI/Badge';
import Button from '@components/smartAdminUI/Button';
import FormSearchBar from '@components/form/formComponents/formSearch/FormSearchBar';
import FieldValidator from '@components/form/formHelpers/FieldValidator';
import FormikInputGroup from '@components/smartAdminUI/input/FormikInputGroup';
import Panel from '@components/smartAdminUI/Panel';
import LayoutGroupWrapper from '@components/form/formComponents/inputGroups/LayoutGroupWrapper';
import RepeatableRoom from '@components/form/formComponents/repeatableRoom/RepeatableRoom';
import SaveAsIncomplete from '@components/form/formHelpers/SaveAsIncomplete';
import FormikFieldUpdater from '@components/form/formHelpers/FormikFieldUpdater';
import OnSubmitValidationError from '@components/form/formikComponents/OnSubmitValidationError';
import FormLoader from '@components/loaders/FormLoader';
import SectionNav from '@components/smartAdminUI/SectionNav';
import StatusBadges from '@components/badges/StatusBadges';
import ListingTypeIcons from '@components/table/icons/ListingTypeIcons';
import GAEventTracking from '@components/tracking/GAEventTracking';

import useRowLock from '@hooks/useRowLock';
import usePrevious from '@hooks/usePrevious';
import useIsMounted from '@hooks/useIsMounted';
import { isUndefined, isNull } from 'lodash';
import FormikField from '@components/form/FormikField';
import { isValidEntity } from '../../../utils/rowLock';

const CreateListingsForm = () => {
  let listingRules;
  const dispatch = useDispatch();
  const isMounted = useIsMounted();
  const formRef = useRef();
  const history = useHistory(); // use for redirection
  const location = useLocation();
  const errPerSection = useSelector(selectErrorsPerSection);
  const orderedSections = useSelector(getOrderedSections);
  const reqPerSection = useSelector(selectRequiredPerSection);
  const agentGroupType = useSelector(state => state.agent.agentGroupType);
  const agentLookup = useSelector(state => state.agent.agentLookup);
  const sortedErrors = useSelector(getSortedErrors);
  const comboSetValueDict = useSelector(selectComboValueDict);
  const currentFormDefinition = useSelector(selectFormDefinition);
  const currCoords = useSelector(state => state.listing.listingCoordinates);
  const currLocation = useMemo(() => location?.pathname, [location]);
  const currListing = useSelector(selectCurrentListing); // holds data related to the edit listing OR the original listing (on copy operation)
  const definitionType = useSelector(state => state.formGeneration.definitionType);
  const editPageName = useSelector(selectEditPageName);
  const formIsLoading = useSelector(state => state.formGeneration.formIsLoading);
  const initialValues = useSelector(selectInitialValues);
  const listingCode = useSelector(selectListingCode); // Get listing code
  const listingRule = useSelector(selectListingRule);
  const listingType = useSelector(selectListingType);
  const lookUpNames = useSelector(selectLookupNames);
  const memoizedInitialValues = useMemo(() => initialValues, [initialValues]);
  const operationType = useSelector(state => state.formGeneration.operationType);
  const isCopyOperation = useSelector(state => state.formGeneration.isCopyOperation);
  const previousOperationType = usePrevious(operationType);
  const pageStructure = useSelector(selectVisiblePageStructure);
  const allFields = useSelector(selectAllFields);
  const selectedListing = useSelector(state => state.listing.selectedListing);
  const listingFormData = useSelector(selectListingFormData);
  const standardStatus = useSelector(selectListingStdStatus);
  const standardStatusVerb = useSelector(selectListingStdStatusVerb);
  const uploadedImages = useSelector(selectPhotoMetaCollection);
  const photoQueueLength = useSelector(selectPhotoQueueLength);
  const editListingInfo = useSelector(selectEditListingHeaderInfo);
  const repeatableSectionCount = useSelector(selectRepeatableSectionCount);
  const sectionsPerRepeatableSection = useSelector(selectSectionsPerRepeatableSection);
  const uploadedSupplements = useSelector(selectUploadedSupplements);
  const roomsPayload = useSelector(selectListingSelectedRooms);
  const openHousesPayload = useSelector(selectListingSelectedOpenHouses);
  const parcelsPayload = useSelector(selectListingSelectedParcels);
  const unitsPayload = useSelector(selectListingSelectedUnits);
  const doorsPayload = useSelector(selectListingSelectedDoors);
  const historyPayload = useSelector(selectListingSelectedHistory);
  const autoSaveCheckLoading = useSelector(state => state.listing.autoSaveCheckLoading);
  const comboLoading = useSelector(state => state.formGeneration.comboIsLoading);
  const dataLoading = useSelector(state => state.listing.selectLoading);
  const formLoading = useSelector(state => state.formGeneration.isLoading);

  const [clearForm, setClearForm] = useState(false); // resets TAP form after closing
  const [currListingType, setCurrListingType] = useState(listingType);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [isFormSubmitted, setIsFormSubmitted] = useState(false);
  const [isFormSavingAsIncomplete, setIsFormSavingAsIncomplete] = useState(false);
  const [isFormSavedAsIncomplete, setIsFormSavedAsIncomplete] = useState(false);
  const [newComboValue, setNewComboValue] = useState({});
  const [runValidation, setRunValidation] = useState(false);
  const [validateFieldName, setValidateFieldName] = useState('');
  const [validateFlag, setValidateFlag] = useState(false);
  const [validationValue, setValidationValue] = useState('');
  const [roomsHaveChanged, setRoomsHaveChanged] = useState(false);
  const [photosHaveChanged, setPhotosHaveChanged] = useState(false);
  const [supplementsHaveChanged, setSupplementsHaveChanged] = useState(false);
  const [openHousesHaveChanged, setOpenHousesHaveChanged] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [progress, setProgress] = useState(((1 / orderedSections.length) * 100));
  const [activeSection, setActiveSection] = useState(1);
  const [incompleteButtonText, setIncompleteButtonText] = useState('Save Incomplete');
  const [autoSaveWorking, setAutoSaveWorking] = useState(true);
  const [preTaxAutoPopListing, setPreTaxAutoPopListing] = useState(null);
  const [wasTaxAutoPopped, setWasTaxAutoPopped] = useState(false);

  const autoSaveData = useSelector(state => state.listing.autoSaveData);
  const autoSaveExists = useSelector(state => state.listing.autoSaveExists);

  const isCreating = useMemo(() => location?.pathname.includes('create'), [location]);
  const isEditing = useMemo(() => location?.pathname.includes('edit'), [location]);
  const gaFormSubmitEventTracker = GAEventTracking('Error Listing Form Submit');
  const gaAutoSaveEventTracker = GAEventTracking('Error Listing Auto Save');
  const gaSaveIncompleteEventTracker = GAEventTracking('Error Listing Save Incomplete');
  const username = useSelector(getUserName);
  const userMLSID = useSelector(getUserMlsId);

  const rollbar = useRollbar();
  useRollbarPerson({ id: userMLSID, username });

  const timedToast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timerProgressBar: true,
  });

  const photosQueued = useMemo(() => {
    if (photoQueueLength > 0) return true;
    return false;
  }, [photoQueueLength]);

  const comboBubbleWrap = { setNewComboValue };

  const autoSaveCriteria = useMemo(() => {
    if (operationType === 'edit' && currListing?.UniqueID) {
      // Look up using ResourceUnique.
      return { ResourceUnique: currListing.UniqueID };
    }
    if (operationType === 'create' && autoSaveData?.length) {
      // Look up using AutoSaveKey.
      const autoSaveRecord = autoSaveData.find(record => !record.ListingId && !record.ParentID && record.ResourceName.toLowerCase() === 'listing' && record.FormName.toLowerCase() === listingCode.toLowerCase() && !record.IsDeleted);
      if (autoSaveRecord) {
        return { AutoSaveKey: autoSaveRecord.UniqueID };
      }
    }
    return null;
  }, [currListing, autoSaveData]);

  const rowLockEntity = useMemo(() => ({ type: 'listing', id: operationType === 'edit' ? currListing?.UniqueID : null }), [currListing]);

  const {
    lockingAuthorized,
    locked,
    lockedBy,
    checked,
    checkStatus,
    createLock,
    removeLock,
    rowLockingEnabled,
  } = useRowLock({ rowLockEntity, isEditOperation: operationType === 'edit', enableRowLocking: operationType === 'edit' });

  const lockReleasedByCurrentUser = useRef(false);

  if (listingRule) listingRules = window.FlexRule.RuntimeEngine.fromJavaScript('OnePlatform', listingRule);

  const autoSaveEnabled = useRef(false);
  const [showAvailableAutoSave, setShowAvailableAutoSave] = useState(false);
  const [isAvailableAutoSaveShown, setIsAvailableAutoSaveShown] = useState(false);
  const [isFormAutoSaving, setIsFormAutoSaving] = useState(false);

  const loadAutoSaveData = () => {
    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Loading Auto Save Data...',
      title: '<span class="fa-stack fa-2x text-success m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-bolt fa-stack-1x"></i></span>',
      showConfirmButton: false,
      timer: 2000,
    });
    dispatch(loadAutoSave(autoSaveCriteria));
  };

  const removeAutoSaveData = () => {
    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      html: 'Discarding Auto Save Data...',
      title: '<span class="fa-stack fa-2x text-danger m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-bolt fa-stack-1x"></i></span>',
      showConfirmButton: false,
      timer: 2000,
    });
    dispatch(removeAutoSave(autoSaveCriteria));
  };

  useEffect(() => {
    if (!currentFormDefinition?.length) return;

    // API call to get all combo box /I'd like to just be able to submit the listingType right here
    // prevent load combos call when there are no lookup names provided
    if (lookUpNames?.lookupNames?.length) {
      dispatch(loadComboBoxOptions(lookUpNames, listingCode));
    }

    // agent userfill should only occur when creating a listing
    if (operationType === 'create') {
      dispatch(getCurrentAgent()); // userfill
      dispatch(checkForAutoSave(autoSaveCriteria));
      setShowAvailableAutoSave(true);
    }

    formRef?.current?.validateForm();
  }, [currentFormDefinition]);

  // enables/disables autoSave
  useEffect(() => {
    autoSaveEnabled.current = operationType === 'create' || (standardStatus === 'I' && !listingCode?.includes('chg'));
  }, [operationType, standardStatus, listingCode]);

  // decision to display autoSave modal.
  useEffect(() => {
    if (operationType === 'create' && autoSaveExists && autoSaveCriteria && showAvailableAutoSave && !isAvailableAutoSaveShown) {
      setIsAvailableAutoSaveShown(true);
      setShowAvailableAutoSave(false);
      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Auto Save Found',
        text: 'There is Auto Save data available, would you like to use it?',
        iconHtml: '<i class="fas fa-bolt fa-xs"/>',
        iconColor: 'var(--theme-success)',
        confirmButtonText: 'Yes',
        denyButtonText: 'No',
        showDenyButton: true,
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn-success btn-lg mr-2 swal-question-listing-autosave-yes',
          denyButton: 'btn-primary btn-lg swal-question-listing-autosave-no',
        },
        showClass: {
          popup: 'slide-in-elliptic-right-bck',
        },
      }).then(result => {
        if (result.isConfirmed) {
          loadAutoSaveData();
          setIsAvailableAutoSaveShown(false);
        } else {
          removeAutoSaveData();
          setIsAvailableAutoSaveShown(false);
        }
      });
    }
  }, [autoSaveExists, autoSaveCriteria]);

  useEffect(() => {
    if (autoSaveCheckLoading || comboLoading || formLoading || dataLoading) {
      setLoadingStatus(true);
    } else {
      setLoadingStatus(false);
    }
  }, [autoSaveCheckLoading, comboLoading, formLoading, dataLoading]);

  /*
    If there is a listing type, and it is different than the one in our state variable,
      then we do the following:
      1) Update our state variable
      2) Dispatch the call that retrieves the dropdown/comboBox options
  */
  useEffect(() => {
    if (listingType && listingType !== currListingType) {
      setCurrListingType(listingType); // Updates the state variable we use for comparison
      if (lookUpNames?.lookupNames?.length) {
        dispatch(loadComboBoxOptions(lookUpNames, listingCode)); // Gets the comboBoxOptions for the new form
      }

      if (operationType === 'create') {
        dispatch(getCurrentAgent()); // userfill
        dispatch(checkForAutoSave(autoSaveCriteria));
        setShowAvailableAutoSave(true);
      }
    }
  }, [listingType, operationType]);

  useEffect(() => {
    // prevent the execution of this effect when going from edit mode to create mode;
    // current component will not be un-mounted yet when [operationType] gets updated in redux.
    if (previousOperationType === 'edit' && operationType === 'create') return;

    if (operationType === 'create' && listingType === currListingType && !isCopyOperation) { // exception case when going from edit to create and the same listing type
      formRef?.current?.resetForm();
      setRunValidation(true);
    }
  }, [currListingType, operationType]);

  // Determines panel header text depending on form 'create' and 'edit' actions
  const getPanelString = useMemo(() => {
    let panelString;
    let listing;

    const listingCodeForPanelString = location.search.split('=')[1];
    const listingTypeForPanelString = (
      <>
        <ListingTypeIcons type={listingCodeForPanelString.toUpperCase()} />
        <span className="ml-2">{listingDict[listingCodeForPanelString]}</span>
      </>
    );

    if (listingTypeForPanelString && (isCreating && currLocation.includes((listingCodeForPanelString).toLowerCase()))) {
      panelString = listingTypeForPanelString;
    } else if (listingTypeForPanelString && !isNull(currListing) && !isNull(selectedListing) && (isEditing && currLocation.includes((listingCodeForPanelString).toLowerCase()))) {
      listing = isUndefined(currListing) || isNull(currListing) ? selectedListing.listing : currListing;
      panelString = (
        <>
          <StatusBadges status={listing.StandardStatusVerb} />
          <span className="mr-2" />
          <ListingTypeIcons type={listing?.PropertyType} />
          <span className="ml-2">{listing?.ListingId}</span>
        </>
      );
    } else if (listingTypeForPanelString && !isNull(selectedListing) && (isEditing && currLocation.includes('chg'))) {
      listing = selectedListing.listing;
      panelString = (
        <>
          { standardStatusVerb && (
            <>
              <StatusBadges status={standardStatusVerb} />
              <span className="mr-2" />
            </>
          )}
          <ListingTypeIcons type={listing?.PropertyType} />
          <span className="ml-2">{listing?.ListingId}</span>
        </>
      );
    } else {
      panelString = <span className="mr-2" />;
    }
    return panelString;
  }, [currLocation, listingType, listingCode, isCreating, isEditing, editPageName, editListingInfo, currListing, selectedListing]);

  useEffect(() => {
    if (Object.keys(newComboValue).length > 0) {
      const newComboKeys = Object.keys(newComboValue);
      if (comboSetValueDict) {
        if (newComboKeys.length > 0) {
          const { fieldName, newValue } = newComboValue;
          const comboMutator = comboSetValueDict[fieldName];
          comboMutator(newValue);
        }
      }
    }
  }, [newComboValue]);

  const buildWhereClause = values => {
    const whereClause = {
      where: {
        clause: [],
      },
    };
    const valueKeys = Object.keys(values);

    valueKeys.forEach(key => {
      if (values[key] && key === 'UniqueID') {
        whereClause.where.clause.push({
          Name: key,
          Value: values[key],
        });
      }
    });

    return whereClause;
  };

  const getDeltas = (formikState, unmodifiedEntry) => {
    const deltas = {};
    if (!formikState) return deltas;
    Object.entries(unmodifiedEntry).map(([key, value]) => {
      if (key in formikState && formikState?.[key] !== value) {
        deltas[key] = formikState?.[key];
      }
      return null;
    });
    return deltas;
  };

  // formik,  // photoMeta.flexMeta
  // this should really just be, has sortOrder or IsDeleted changed.
  // URL should not change.
  const getPhotoDeltas = (formikPhoto, unmodifiedEntry) => {
    const deltas = {};
    if (!formikPhoto) return deltas;
    if (formikPhoto.IsDeleted !== unmodifiedEntry.flexMeta.IsDeleted) deltas.IsDeleted = formikPhoto.IsDeleted;
    if (formikPhoto.Description !== unmodifiedEntry.flexMeta.Description) deltas.Description = formikPhoto.Description;
    if (formikPhoto.SortOrder !== unmodifiedEntry.flexMeta.SortOrder) deltas.SortOrder = formikPhoto.SortOrder;
    return deltas;
  };

  /*
  I chose to keep UI state (formik) and file upload state (redux) separate, then combine on form validation.
  This should be more readable and perform better than storing all state in Formik. Consider this approach in refactor.
  */
  const mergeSupplementData = (uiState, uploadList = [], isAutoSave = false) => {
    const mergedSupplements = uploadList.map((file, index) => {
      if (!file) return [];
      const deltas = getDeltas(uiState?.[file.uid], file);
      if (file.UniqueID) {
        return {
          ...(isAutoSave ? file : {}),
          ...deltas,
          UniqueID: file.UniqueID,
          IsDeleted: file.IsDeleted,
          SortOrder: index + 1,
        };
      }
      return {
        // FileName: file.displayName, currently not used by BE/FR
        ...file,
        ...uiState?.[file.uid], // SupplementType and Description
      };
    });
    return mergedSupplements;
  };

  // uploadList is now photoMetaCollection
  // formikState submitted to autoSave as formikStateObject mimics submit configuration
  // !IsError && !IsUploaded
  const mergePhotoData = (formikState, photoMetaCollection = [], isAutoSave = false) => {
    if (!photoMetaCollection) return [];
    let deltasExist = false;
    const mergedPhotos = photoMetaCollection.map(image => {
      const isError = image.IsError;
      const isUploaded = image.IsUploaded;
      if (image.flexMeta?.UniqueID) { // image exists in flexRule DB
        // Handle existing photo.
        const { SortOrder, IsDeleted } = formikState[image.key];
        if (IsDeleted) {
          // Mark photo as deleted.
          return {
            ...(isAutoSave ? formikState[image.key] : {}),
            UniqueID: image.flexMeta.UniqueID,
            SortOrder: image.SortOrder,
            IsDeleted,
          };
        }
        if (isError && !IsDeleted) {
          // do not calculate deltas
          return {
            ...(isAutoSave ? formikState[image.key] : {}),
            UniqueID: image.flexMeta.UniqueID,
            SortOrder,
            IsDeleted,
          };
        }
        // Update photo.
        //
        const deltas = getPhotoDeltas(formikState[image.key], image);
        if (Object.keys(deltas).length !== 0) deltasExist = true;
        return {
          ...(isAutoSave ? formikState[image.key] : {}),
          SortOrder,
          IsDeleted,
          ...deltas,
          UniqueID: image.flexMeta.UniqueID,
        };
      }
      // Handle new photo.
      const {
        URL,
        Description,
        SortOrder,
        IsDeleted,
      } = formikState[image.key];
      if (isError) return null;
      if (isAutoSave && !isUploaded) return null;

      return {
        ...(isAutoSave ? formikState[image.key] : {}),
        URL,
        Description,
        SortOrder,
        IsDeleted,
      };
    }).filter(photo => photo !== null);
    // calculates sort order for live photos, adjusts sort order in case of errored photos ejected from payload
    // eslint-disable-next-line no-return-assign
    mergedPhotos.filter(p => !p.IsDeleted).sort((p1, p2) => p1.SortOrder - p2.SortOrder).forEach((photo, index) => photo.SortOrder = index + 1);
    return [mergedPhotos, deltasExist];
  };

  const mergeFormikData = (formikState, uploadList, isAutoSave = false, sectionName) => {
    let mergedData = [];
    if (uploadList) {
      mergedData = uploadList.flatMap(entry => {
        if (!entry) return [];
        let entryKey = '';
        Object.entries(formikState).map(([key, value]) => {
          if (entry.UniqueID && entry.UniqueID === value.UniqueID) entryKey = key;
          if (entry.uid) entryKey = entry.uid;
          return null;
        });
        const formikValues = formikState[entryKey];
        if (!formikValues) return [];
        const deltas = getDeltas(formikValues, entry);
        if (entry.UniqueID) {
          return {
            ...(isAutoSave ? entry : {}),
            ...deltas,
            UniqueID: entry.UniqueID,
            SortOrder: formikValues.SortOrder,
            IsDeleted: formikValues.IsDeleted,
          };
        }
        // this is an else, the linter removes the else {}
        const newEntry = { ...formikValues };
        if (newEntry.IsDeleted) return [];
        // delete newEntry.IsDeleted;
        delete newEntry.IsLive;
        return newEntry;
        // end of else
      });
    }
    return mergedData;
  };

  const buildRepeatables = values => {
    const submitValues = {};

    // Photos and Supplements are being handled separately.

    if (values.Rooms) {
      const entries = [];
      Object.entries(values.Rooms).map(([keyField]) => {
        const fieldExists = roomsPayload.find(room => room.UniqueID && room.UniqueID === values.Rooms[keyField]?.UniqueID);
        if (fieldExists === undefined) return entries.push({ ...values.Rooms[keyField] });
        if (fieldExists) return entries.push({ ...fieldExists });
        return null;
      });
      submitValues.Rooms = entries;
    }

    if (values.OpenHouses) {
      const entries = [];
      Object.entries(values.OpenHouses).map(([keyField]) => {
        const fieldExists = openHousesPayload.find(openHouse => openHouse.UniqueID && openHouse.UniqueID === values.OpenHouses[keyField]?.UniqueID);
        if (fieldExists === undefined) return entries.push({ ...values.OpenHouses[keyField] });
        if (fieldExists) return entries.push({ ...fieldExists });
        return null;
      });
      submitValues.OpenHouses = entries;
    }

    if (values.Doors) {
      const entries = [];
      Object.entries(values.Doors).map(([keyField]) => {
        const fieldExists = doorsPayload.find(door => door.UniqueID && door.UniqueID === values.Doors[keyField]?.UniqueID);
        if (fieldExists === undefined) return entries.push({ ...values.Doors[keyField] });
        if (fieldExists) return entries.push({ ...fieldExists });
        return null;
      });
      submitValues.Doors = entries;
    }

    if (values.Parcels) {
      const entries = [];
      Object.entries(values.Parcels).map(([keyField]) => {
        const fieldExists = parcelsPayload.find(parcel => parcel.UniqueID && parcel.UniqueID === values.Parcels[keyField]?.UniqueID);
        if (fieldExists === undefined) return entries.push({ ...values.Parcels[keyField] });
        if (fieldExists) return entries.push({ ...fieldExists });
        return null;
      });
      submitValues.Parcels = entries;
    }

    if (values.Units) {
      const entries = [];
      Object.entries(values.Units).map(([keyField]) => {
        const fieldExists = unitsPayload.find(unit => unit.UniqueID && unit.UniqueID === values.Units[keyField]?.UniqueID);
        if (fieldExists === undefined) return entries.push({ ...values.Units[keyField] });
        if (fieldExists) return entries.push({ ...fieldExists });
        return null;
      });
      submitValues.Units = entries;
    }

    if (values.History) {
      const entries = [];
      Object.entries(values.History).map(([keyField]) => {
        const fieldExists = historyPayload.find(hist => hist.UniqueID && hist.UniqueID === values.History[keyField]?.UniqueID);
        if (fieldExists === undefined) return entries.push({ ...values.History[keyField] });
        if (fieldExists) return entries.push({ ...fieldExists });
        return null;
      });
      submitValues.History = entries;
    }

    return { ...submitValues };
  };

  const buildCreateValues = values => {
    const submitValues = {};
    Object.entries(values).map(([key, value]) => {
      if (!isEmpty(value)) {
        submitValues[key] = value;
      } else if (typeof value === 'number') { // for bit fields
        submitValues[key] = value;
      }
      return null;
    });
    const repeatableValues = buildRepeatables(values);
    return { ...submitValues, ...repeatableValues };
  };

  const convertDateToUTC = date => new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds());

  const getDateValue = value => {
    let formattedDate;
    if (value) {
      const dateUTC = convertDateToUTC(new Date(value));
      const year = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(dateUTC);
      const month = new Intl.DateTimeFormat('en', { month: '2-digit' }).format(dateUTC);
      const day = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(dateUTC);
      formattedDate = `${year}-${month}-${day}`;
    }
    return formattedDate ?? '';
  };

  const getBitValue = value => {
    let bitValue;
    if (value != null && value !== '') {
      if (typeof value === 'string') {
        bitValue = value === 'true' ? 1 : 0;
      } else if (typeof value === 'boolean') {
        bitValue = value === true ? 1 : 0;
      } else if (typeof value === 'number') {
        bitValue = value;
      }
    }
    return bitValue ?? '';
  };

  const getStringValue = value => value ?? '';

  const getNumericValue = value => (value != null && value !== '' ? Number(value) : '');

  const getComboBoxValue = value => value ?? '';

  // TODO: move to utility module (used by listing/agent/office)
  const valueHasChanged = (fieldName, oldValue, newValue) => {
    const inputType = allFields[fieldName]?.InputType;
    const dataType = allFields[fieldName]?.DataType;
    switch (inputType) {
      case 'TextInput':
        if (dataType === 'datetime' || dataType === 'date') {
          return (getDateValue(oldValue) !== getDateValue(newValue));
        }
        if (dataType === 'bit') {
          return (getBitValue(oldValue) !== getBitValue(newValue));
        }
        if (dataType === 'string') {
          return (getStringValue(oldValue) !== getStringValue(newValue));
        }
        if (dataType === 'numeric' || dataType === 'decimal' || dataType === 'integer') {
          return (getNumericValue(oldValue) !== getNumericValue(newValue));
        }
        return (oldValue !== newValue);

      case 'ComboBox':
        return (getComboBoxValue(oldValue) !== getComboBoxValue(newValue));

      default:
        return (oldValue !== newValue);
    }
  };

  // finding deltas and adding
  const buildUpdateValues = values => {
    const submitValues = {};
    const oldValues = preTaxAutoPopListing ?? selectedListing.listing;

    Object.entries(values).forEach(([key, value]) => {
      if (Object.keys(oldValues).includes(key)) {
        if (valueHasChanged(key, oldValues[key], value)) {
          submitValues[key] = value;
        }
      } else if ((!isEmpty(value) || typeof value === 'number')) {
        submitValues[key] = value;
      }
    });

    const repeatableValues = buildRepeatables(values);
    return { ...submitValues, ...repeatableValues };
  };

  const buildAutoSaveValues = values => {
    const submitValues = {};
    Object.entries(values).map(([key, value]) => {
      if (allFields[key]?.DataType === 'bit' && value !== null && value !== '') {
        submitValues[key] = Boolean(value);
      } else if (!isEmpty(value) || typeof value === 'number') {
        submitValues[key] = value;
      }
      return null;
    });
    const repeatableValues = buildRepeatables(values);
    return { ...submitValues, ...repeatableValues };
  };

  const generatePostBody = (values, isAutoSave = false) => {
    let submitValues = {};
    let autoSaveValues = {};
    let nonAutoSaveValues = {};

    if (isAutoSave) {
      autoSaveValues = buildAutoSaveValues(values);
    } else {
      nonAutoSaveValues = operationType === 'edit'
        ? buildUpdateValues(values)
        : buildCreateValues(values);
      autoSaveValues = autoSaveExists ? buildAutoSaveValues(values) : {};
    }
    submitValues = { ...nonAutoSaveValues, ...autoSaveValues };

    const {
      Rooms, OpenHouses, Doors, Parcels, Units, History, ...listing
    } = submitValues;
    const supplementsGroup = mergeSupplementData(values.Supplements, uploadedSupplements, isAutoSave);
    const [photosGroup, photosChanged] = mergePhotoData(values.Photos, uploadedImages, isAutoSave);
    const roomsGroup = mergeFormikData(values.Rooms, Rooms, isAutoSave, 'Rooms');
    const openHouseGroup = mergeFormikData(values?.OpenHouses, OpenHouses, isAutoSave);
    const doorsGroup = mergeFormikData(values?.Doors, Doors, isAutoSave);
    const unitsGroup = mergeFormikData(values?.Units, Units, isAutoSave);
    const parcelsGroup = mergeFormikData(values?.Parcels, Parcels, isAutoSave);
    const historyGroup = mergeFormikData(values?.History, History, isAutoSave);
    // submitted listing type is used as task edit forms don't load a complete form definition and need to retrieve the property type elsewhere
    const submittedPropertyType = selectedListing?.listing?.PropertyType ?? listingType;
    const uniqueId = selectedListing?.listing ? { UniqueID: selectedListing.listing.UniqueID } : '';
    const postBody = {
      formName: listingCode,
      data: {
        currentStandardStatus: selectedListing?.listing?.StandardStatus ?? 'I',
        propertyType: submittedPropertyType,
        listing: isAutoSave
          ? {
            ...uniqueId,
            ...listing,
          }
          : {
            [submittedPropertyType]: {
              ...uniqueId,
              ...listing,
            },
          },
        roomsHaveChanged,
        photosHaveChanged,
        supplementsHaveChanged,
        openHousesHaveChanged,
        photos: photosGroup,
        rooms: roomsGroup,
        supplements: supplementsGroup,
        openHouses: openHouseGroup,
        doors: doorsGroup,
        units: unitsGroup,
        parcels: parcelsGroup,
        history: historyGroup,
      },
    };
    return postBody;
  };

  const handleAutoSave = async () => {
    if (!(operationType === 'create') && !(operationType === 'edit')) return;
    if (!autoSaveEnabled.current) return;
    if (isAvailableAutoSaveShown) return;
    if (autoSaveCheckLoading) return;
    if (isFormSubmitting || isFormSavingAsIncomplete || isFormAutoSaving) return;
    if (isFormSubmitted || isFormSavedAsIncomplete) return;

    setIsFormAutoSaving(true);

    const { current: currForm } = formRef;
    const autoSaveBody = generatePostBody(currForm?.values, true);

    autoSaveBody.data.photos = autoSaveBody?.data?.photos ?? [];
    let saved = false;
    try {
      if (!autoSaveBody.data?.listing?.StandardStatus) autoSaveBody.data.listing.StandardStatus = 'I';
      if (operationType === 'edit') {
        const saveData = {
          FormName: autoSaveBody.formName,
          ResourceData: autoSaveBody.data,
          ResourceId: currListing.ListingId,
          ResourceUnique: currListing.UniqueID,
        };
        saved = autoSaveExists && autoSaveCriteria
          ? await dispatch(updateAutoSave(saveData))
          : await dispatch(saveAutoSave(saveData));
      } else if (operationType === 'create') {
        const saveData = {
          FormName: autoSaveBody.formName,
          ResourceData: autoSaveBody.data,
          AutoSaveKey: autoSaveCriteria?.AutoSaveKey,
        };
        saved = autoSaveExists && autoSaveCriteria
          ? await dispatch(updateAutoSave(saveData))
          : await dispatch(saveAutoSave(saveData));

        if (saved && !autoSaveCriteria) {
          dispatch(checkForAutoSave());
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error: Listing handleAutoSave', err, autoSaveBody);
    }

    // If the component has been un-mounted meanwhile, we don't need to update local state anymore, nor display subsequent alerts.
    if (!isMounted) return;

    setIsFormAutoSaving(false);

    if (saved) {
      setShowAvailableAutoSave(false);
      setIsAvailableAutoSaveShown(false);
      setAutoSaveWorking(true);
    } else {
      setAutoSaveWorking(false);
    }

    if (!autoSaveWorking) {
      if (operationType === 'edit') gaAutoSaveEventTracker(`Edit - ${currListing.ListingId}`, `${username}`);
      if (operationType === 'create') gaAutoSaveEventTracker('Create', `${username}`);
      rollbar.error('Error: Listing Auto Save', 'Auto Save Failed', autoSaveBody);

      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: 'Auto Save Failed',
        text: 'Your data could not be Auto Saved at this time, please try to Save As Incomplete.',
        iconHtml: '<i class="fas fa-bolt fa-xs"/>',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-listing-autosave',
        },
      });
    }
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    if (!(operationType === 'create') && !(operationType === 'edit')) return;
    if (isFormSubmitting || isFormSavingAsIncomplete || isFormAutoSaving) return;
    if (isFormSubmitted || isFormSavedAsIncomplete) return;

    // Integration pending API refactor: ideally we will leave data scrubbing, building where clauses, etc. to the BE
    const postBody = generatePostBody(values);
    let listingId;

    try {
      // If the component has been un-mounted meanwhile, we don't need to update local state anymore, nor display subsequent alerts.
      if (!isMounted) return;

      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        icon: 'question',
        iconColor: 'var(--theme-warning)',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-warning btn-lg mr-2 swal-question-listing-submit-yes',
          denyButton: 'btn btn-primary btn-lg swal-question-listing-submit-no',
        },
        confirmButtonText: 'Yes, Continue',
        denyButtonText: 'No, Cancel',
        showDenyButton: true,
        title: 'Submit Listing?',
        text: 'Your changes will be reflected in Matrix™ within 10-15 minutes and on Realtor.ca® within 10-30 minutes.',
      }).then(async choice => {
        if (choice.isConfirmed) {
          setIsFormSubmitting(true);

          if (operationType === 'create') {
            const result = await apiService.Listing.CreateListing({
              listingRequest: postBody,
              autoSaveRequest: autoSaveExists && autoSaveCriteria ? autoSaveCriteria : null,
              rowLockRequest: null,
            });
            listingId = result?.Data?.ListingId;
          } else if (operationType === 'edit') {
            listingId = currListing?.ListingId;
            const whereClause = currListing ? buildWhereClause(currListing) : null;
            await apiService.Listing.UpdateListing({
              listingRequest: {
                ...postBody,
                ...whereClause, // BE requires where clause in EDIT operation (but not CREATE)
              },
              autoSaveRequest: autoSaveExists && autoSaveCriteria ? autoSaveCriteria : null,
              rowLockRequest: rowLockingEnabled ? { type: 'listing', id: currListing.UniqueID } : null,
            });
          }
          dispatch(formSubmitted());
          if (autoSaveExists && autoSaveCriteria) {
            removeAutoSaveData();
          }
          removeLock();
          lockReleasedByCurrentUser.current = true;

          setIsFormSubmitting(false);
          setSubmitting(false);
          setIsFormSubmitted(true);

          const message = listingCode === 'chgDel' ? 'Listing Deleted' : 'Listing Submitted';
          const locationString = listingCode === 'chgDel' ? '/' : `/listings/view?ListingID=${listingId}`;

          Swal.fire({
            allowEnterKey: false,
            allowEscapeKey: false,
            allowOutsideClick: false,
            position: 'center',
            title: message,
            icon: 'success',
            iconColor: 'var(--theme-success)',
            showConfirmButton: false,
            timer: 3000,
          }).then(() => {
            history.push(locationString);
          });
        }
      });
    } catch (error) {
      // If the component has been un-mounted meanwhile, we don't need to update local state anymore, nor display subsequent alerts.
      if (!isMounted) return;

      setIsFormSubmitting(false);
      setSubmitting(false);
      setIsFormSubmitted(false);
      const message = listingCode === 'chgDel' ? 'Listing Not Deleted' : 'Listing Not Submitted';
      const messageText = listingCode === 'chgDel' ? 'Listing could not be deleted' : 'Listing could not be submitted';

      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        title: message,
        text: `${messageText} at this time. Please contact support.`,
        icon: 'error',
        iconColor: 'var(--theme-danger)',
        confirmButtonText: 'OK',
        buttonsStyling: false,
        customClass: {
          confirmButton: 'btn btn-danger btn-lg swal-fail-listing-submit',
        },
      });
      if (listingCode === 'chgDel') {
        gaFormSubmitEventTracker(`Change to Delete - ${listingId}`, `${username}`);
        rollbar.error('Error: Listing Change to Delete', error, postBody);
      } else {
        if (operationType === 'create') gaFormSubmitEventTracker(`Save Create - ${listingId}`, `${username}`);
        if (operationType === 'edit') gaFormSubmitEventTracker(`Save Edit - ${listingId}`, `${username}`);
        rollbar.error('Error: Listing Form Submit', error, postBody);
      }
    }
  };

  const handleSaveIncomplete = async () => {
    if (!(operationType === 'create') && !(operationType === 'edit')) return;
    if (isFormSubmitting || isFormSavingAsIncomplete || isFormAutoSaving) return;
    if (isFormSubmitted || isFormSavedAsIncomplete) return;

    const { current: currForm } = formRef;
    const postBody = generatePostBody(currForm?.values);
    let listingId;

    // If the component has been un-mounted meanwhile, we don't need to update local state anymore, nor display subsequent alerts.
    if (!isMounted) return;

    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      icon: 'question',
      iconColor: 'var(--theme-warning)',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-warning btn-lg mr-2 swal-question-listing-saveIncomplete-yes',
        denyButton: 'btn btn-primary btn-lg swal-question-listing-saveIncomplete-no',
      },
      confirmButtonText: 'Yes, Continue',
      denyButtonText: 'No, Cancel',
      showDenyButton: true,
      title: 'Save As Incomplete?',
      html: '<p>Your changes will be reflected in Matrix™ within 10-15 minutes.</p>'
            + '<br>'
            + '<p><strong>Reminder:</strong> Use of MLS® listing numbers for advertising and promotion is only permitted once the listing has been activated on the MLS® System.</p>',
    }).then(async choice => {
      if (choice.isConfirmed) {
        setIsFormSavingAsIncomplete(true);
        setIncompleteButtonText('Saving...');
        try {
          if (operationType === 'create') {
            const result = await apiService.Listing.SaveIncomplete({
              listingRequest: postBody,
              autoSaveRequest: autoSaveExists && autoSaveCriteria ? autoSaveCriteria : null,
              rowLockRequest: null,
            });
            listingId = result?.Data?.ListingId;
          } else if (operationType === 'edit') {
            listingId = currListing?.ListingId;
            const whereClause = currListing ? buildWhereClause(currListing) : null;
            await apiService.Listing.UpdateIncomplete({
              listingRequest: {
                ...postBody,
                ...whereClause, // BE requires where clause in EDIT operation (but not CREATE)
              },
              autoSaveRequest: autoSaveExists && autoSaveCriteria ? autoSaveCriteria : null,
              rowLockRequest: rowLockingEnabled ? { type: 'listing', id: currListing.UniqueID } : null,
            });
          }
        } catch (error) {
          // If the component has been un-mounted meanwhile, we don't need to update local state anymore, nor display subsequent alerts.
          if (!isMounted) return;

          setIsFormSavingAsIncomplete(false);
          setIncompleteButtonText('Save Incomplete');
          setIsFormSavedAsIncomplete(false);

          Swal.fire({
            allowEnterKey: false,
            allowEscapeKey: false,
            allowOutsideClick: false,
            position: 'center',
            title: 'Listing Not Saved As Incomplete',
            text: 'Listing could not be saved at this time. Please contact support',
            icon: 'error',
            iconColor: 'var(--theme-danger)',
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-danger btn-lg swal-fail-listing-saveIncomplete',
            },
          });
          if (operationType === 'create') gaSaveIncompleteEventTracker(`Save Incomplete Create - ${listingId}`, `${username}`);
          if (operationType === 'edit') gaSaveIncompleteEventTracker(`Save Incomplete Edit - ${listingId}`, `${username}`);
          rollbar.error('Error: Listing Save Incomplete', error, postBody);

          return;
        }

        dispatch(formSubmitted());
        lockReleasedByCurrentUser.current = true;

        setIsFormSavingAsIncomplete(false);
        setIncompleteButtonText('Save Incomplete');
        setIsFormSavedAsIncomplete(true);

        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Listing Saved As Incomplete',
          icon: 'success',
          iconColor: 'var(--theme-success)',
          showConfirmButton: false,
          timer: 3000,
        }).then(() => {
          history.push(listingId == null ? '/' : `/listings/view?ListingID=${listingId}`);
        });
      }
    });
  };

  const onClickSubmit = (values, actions) => {
    if (rowLockingEnabled) return checkStatus(async () => handleSubmit(values, actions)); // check lock status before submitting
    return handleSubmit(values, actions);
  };

  const onClickSaveIncomplete = (values, actions) => {
    if (rowLockingEnabled) return checkStatus(async () => handleSaveIncomplete()); // check lock status before submitting

    return handleSaveIncomplete(values, actions);
  };

  const getRepeatableChanged = (formikGroup, unmodifiedGroup) => {
    /* does origin have this item?
      does any prop of this item not match origin?
     */
    let hasChanged = false;
    if (formikGroup.length !== unmodifiedGroup.length) return true;
    formikGroup.forEach(item => {
      if (hasChanged) return;
      if (item.UniqueID) {
        // room/door/parcel, openhouse?
        const umItem = unmodifiedGroup.find(umi => umi.UniqueID === item.UniqueID);
        Object.keys(umItem).forEach(itemkey => {
          if (hasChanged) return;
          if (umItem?.[itemkey]) {
            if (umItem[itemkey] !== item[itemkey]) hasChanged = true;
          }
        });
        // see that every key's value in formikElement matches umEl
      } else if (item.URL) {
        // photo or supp
        const umItem = unmodifiedGroup.find(umi => umi.URL === item.URL);
        Object.keys(item).forEach(itemkey => {
          if (hasChanged) return;
          if (umItem?.[itemkey]) {
            if (umItem[itemkey] !== item[itemkey]) hasChanged = true;
          }
        });
      } else {
        // item has neither url or uniqueId;
        hasChanged = true;
      }
    });
    return hasChanged;
  };

  const getChanged = (unModGroup, photosGroup, roomsGroup, supplementsGroup, openHousesGroup) => {
    let photosChanged = false;
    let supplementsChanged = false;
    let roomsChanged = false;
    let openHousesChanged = false;

    photosChanged = getRepeatableChanged(photosGroup, unModGroup.umPhotos);
    supplementsChanged = getRepeatableChanged(supplementsGroup, unModGroup.umSupplements);
    roomsChanged = getRepeatableChanged(roomsGroup, unModGroup.umRooms);
    openHousesChanged = getRepeatableChanged(openHousesGroup, unModGroup.umOpenHouses);

    const photosChangedBool = photosChanged;
    photosChanged = photosChanged ? 1 : 0;
    const supplementsChangedBool = supplementsChanged;
    supplementsChanged = supplementsChanged ? 1 : 0;
    const roomsChangedBool = roomsChanged;
    roomsChanged = roomsChanged ? 1 : 0;
    const openHousesChangedBool = openHousesChanged;
    openHousesChanged = openHousesChanged ? 1 : 0;

    return [photosChanged, photosChangedBool, supplementsChanged, supplementsChangedBool, roomsChanged, roomsChangedBool, openHousesChanged, openHousesChangedBool];
  };

  // TODO: Refactor
  // converts repeatable data to format required by validation scripts
  // validation is running before the form has even loaded the listing data
  const transformRepeatable = values => {
    let newValues = {};

    // added if statement to compliment the onMount validation
    if (selectedListing?.listing) { // dealing with a saved listing
      newValues = {
        Photos: values.Photos,
        Rooms: values.Rooms,
        Supplements: values.Supplements,
        OpenHouses: values.OpenHouses,
        ...values,
        unmodifiedListing: { ...values, ...selectedListing.listing },
      };
    } else { // new listing
      newValues = { ...values, unmodifiedListing: {} }; // supply values and append empty unMod.
    }

    const {
      Photos, Rooms, Supplements, OpenHouses, unmodifiedListing, ...otherValues
    } = newValues;
    const photosGroup = Photos
      ? Object.keys(Photos).map(key => (!Photos[key].IsDeleted) && Photos[key])
      : [];
    const roomsGroup = Rooms
      ? Object.keys(Rooms).map(key => (!Rooms[key].IsDeleted) && Rooms[key]).filter(r => r !== false)
      : [];
    // see comments above
    const supplementsGroup = Supplements
      ? Object.keys(Supplements).map(key => (!Supplements[key].IsDeleted) && Supplements[key])
      : [];
    const openHousesGroup = OpenHouses
      ? Object.keys(OpenHouses).map(key => (!OpenHouses[key].IsDeleted) && OpenHouses[key])
      : [];

    const initKeys = Object.keys(memoizedInitialValues); // add in blank or null values for blank form validation
    initKeys.forEach(k => {
      if (otherValues[k] || otherValues[k] === 0) return;
      otherValues[k] = memoizedInitialValues[k];
    });

    return {
      listing: otherValues, photos: photosGroup, rooms: roomsGroup, supplements: supplementsGroup, unmodifiedListing, openHouses: openHousesGroup,
    };
  };

  const myValidateForm = values => {
    const errors = {};

    if (listingRules) {
      try {
        const {
          listing, photos, rooms, supplements, openHouses, unmodifiedListing,
        } = transformRepeatable(values);

        const unModGroup = {
          umPhotos: selectedListing?.photos ?? [],
          umRooms: selectedListing?.rooms ?? [],
          umSupplements: selectedListing?.supplements ?? [],
          umOpenHouses: selectedListing?.openHouses ?? [],
          umParcels: selectedListing?.parcels ?? [],
          umDoors: selectedListing?.doors ?? [],
        };
        // B: for new validation files, some listing types do not require certain subtables.
        // B: the current order is listing, unmodifiedListing, photos, supplements, rooms

        const [photosChanged, photosChangedBool, supplementsChanged, supplementsChangedBool, roomsChanged, roomsChangedBool, openHousesChanged, openHousesChangedBool] = getChanged(unModGroup, photos, rooms, supplements, openHouses);
        setPhotosHaveChanged(photosChangedBool);
        setSupplementsHaveChanged(supplementsChangedBool);
        setRoomsHaveChanged(roomsChangedBool);
        setOpenHousesHaveChanged(openHousesChangedBool);

        const formName = listingType ?? editPageName;
        let res;
        switch (formName) {
          case 'Rental':
          case 'Residential':
          case 'Multifamily':
          case 'MobileHome':
            res = listingRules?.run(photosChanged, supplementsChanged, roomsChanged, openHousesChanged, listing, unmodifiedListing, photos, supplements, rooms, openHouses);
            break;
          case 'Commercial':
          case 'Farm':
          case 'Land':
            res = listingRules?.run(photosChanged, supplementsChanged, roomsChanged, openHousesChanged, listing, unmodifiedListing, photos, openHouses);
            break;
          case 'OpenHouses':
            res = listingRules?.run(photosChanged, supplementsChanged, roomsChanged, openHousesChanged, listing, unmodifiedListing, openHouses);
            break;
          default:
            res = listingRules?.run(listing, unmodifiedListing, photos, supplements, rooms);
            break;
        }

        const { fieldValidationResult, listingIsValid } = res.context.variableContainer;
        dispatch(setValidationResult(fieldValidationResult));
        if (!listingIsValid) {
          errors.listingIsValid = listingIsValid;
        }
      } catch (er) {
        rollbar.error('Error: Listing Validation', er, values);
      }
    }
    return errors;
  };

  const validateFigField = (figFieldName, figValidationValue) => {
    setValidateFieldName(figFieldName);
    setValidateFlag(true);
    setValidationValue(figValidationValue);
  };

  const fieldsUpdated = () => {
    setRunValidation(true);
  };

  useEffect(() => {
    setRunValidation(true);
  }, [repeatableSectionCount]);

  useEffect(() => {
    if (runValidation) {
      formRef?.current?.validateForm();
      setRunValidation(false);
    }
  }, [runValidation]);

  // Passing data from TAP table to the form
  const listingSelected = data => {
    if (data) {
      const dispatchData = {
        ...data,
        currentStatus: listingFormData.StandardStatus,
      };
      Swal.fire({
        allowEnterKey: false,
        allowEscapeKey: false,
        allowOutsideClick: false,
        position: 'center',
        html: 'Loading Tax Autopop Data...',
        title: '<span class="fa-stack fa-2x text-success m-3"><i class="fal fa-circle-notch fa-spin fa-stack-2x"></i><i class="fas fa-magic fa-stack-1x"></i></span>',
        showConfirmButton: false,
        timer: 2000,
      });
      if (!isNull(selectedListing)) {
        setPreTaxAutoPopListing(selectedListing.listing);
        setWasTaxAutoPopped(true);
      }
      dispatch(loadTaxAutoPopSelected(dispatchData));
    }
  };

  // prevents form from submitting on enter key press
  const handleEnterKeyDown = keyEvent => {
    if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
      keyEvent.preventDefault();
    }
  };

  // Handle custom header for supplements and photo sections
  const getCustomHeader = section => {
    if (section.sectionName === 'Supplements' || section.sectionName === 'Photos' || section.sectionName === 'Rooms' || section.sectionName === 'OpenHouses'
      || section.sectionName === 'Doors' || section.sectionName === 'Parcels' || section.sectionName === 'Units' || section.sectionName === 'History') {
      return (
        <>
          {section.sectionLabel}
          <Badge bg="Secondary" marginRight placeRight bw="width-3">
            {sectionsPerRepeatableSection[section.sectionName]
              ? sectionsPerRepeatableSection[section.sectionName].length
              : 0}
          </Badge>
        </>
      );
    }
    return section.sectionLabel;
  };

  // Will redirect user to view listings page or home page depending on the page operation type
  const handleDiscardChanges = e => {
    Swal.fire({
      allowEnterKey: false,
      allowEscapeKey: false,
      allowOutsideClick: false,
      position: 'center',
      title: 'Discard Changes?',
      html: 'Your unsaved changes, including Auto Save data will be discarded.',
      iconHtml: '<i class="far fa-trash-alt fa-xs"/>',
      iconColor: 'var(--theme-danger)',
      buttonsStyling: false,
      showDenyButton: true,
      confirmButtonText: 'Yes, Continue',
      denyButtonText: 'No, Cancel',
      customClass: {
        confirmButton: 'btn btn-danger btn-lg mr-2 swal-question-listing-discard-yes',
        denyButton: 'btn btn-primary btn-lg swal-question-listing-discard-no',
      },
    }).then(result => {
      if (result.isConfirmed) {
        if (operationType) {
          e.preventDefault();
          if (autoSaveExists && autoSaveCriteria) {
            removeAutoSaveData();
          }
          removeLock();
          lockReleasedByCurrentUser.current = true;
          history.push(operationType === 'create' ? '/' : '/listings/view');
        }
      }
    });
  };

  const handlePreviousClick = () => {
    const tabNav = document.querySelector('.tab-nav');
    const tabActive = tabNav.querySelector('.active');
    if (tabActive) {
      const navItem = tabActive.parentNode;
      const previous = navItem.previousElementSibling;
      if (previous) {
        previous.querySelector('a').click();
        window.scrollTo(0, 0);
        setProgress(progress - ((1 / orderedSections.length) * 100));
        setActiveSection(activeSection - 1);
      }
    }
  };

  const handleNextClick = () => {
    const tabNav = document.querySelector('.tab-nav');
    const tabActive = tabNav.querySelector('.active');
    if (tabActive) {
      const navItem = tabActive.parentNode;
      const next = navItem.nextElementSibling;
      if (next) {
        next.querySelector('a').click();
        window.scrollTo(0, 0);
        setProgress(progress + ((1 / orderedSections.length) * 100));
        setActiveSection(activeSection + 1);
      }
    }
  };

  // updates row lock and displays an alert when changes are being overwritten by another user
  useEffect(() => {
    const tryRegainRowLock = async () => {
      try {
        const created = await createLock();
        if (!created) {
          Swal.fire({
            allowEnterKey: false,
            allowEscapeKey: false,
            allowOutsideClick: false,
            position: 'center',
            title: 'Lock Lost',
            text: 'Your lock was lost and could not be regained.',
            iconHtml: '<i class="far fa-lock-open fa-xs"/>',
            iconColor: 'var(--theme-warning)',
            confirmButtonText: 'OK',
            buttonsStyling: false,
            customClass: {
              confirmButton: 'btn btn-warning btn-lg swal-warning-listing-lockLost',
            },
          }).then(resp => {
            if (resp.value) {
              history.push('/listings/view');
            }
          });
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error: Listing tryRegainRowLock', err);
      }
    };

    if (operationType === 'edit' && !lockReleasedByCurrentUser.current) {
      if (checked && !locked) {
        tryRegainRowLock();
        return;
      }

      if (locked && !lockingAuthorized) {
        Swal.fire({
          allowEnterKey: false,
          allowEscapeKey: false,
          allowOutsideClick: false,
          position: 'center',
          title: 'Changes Overwritten',
          text: `Your changes have been overwritten by ${lockedBy}.`,
          iconHtml: '<i class="far fa-lock-open fa-xs"/>',
          iconColor: 'var(--theme-danger)',
          confirmButtonText: 'OK',
          buttonsStyling: false,
          customClass: {
            confirmButton: 'btn btn-danger btn-lg swal-warning-listing-changesOverwritten',
          },
        }).then(resp => {
          if (resp.value) {
            history.push('/listings/view');
          }
        });
      }
    }
  }, [locked, lockingAuthorized, lockedBy, operationType]);

  // used to return FormikField Components only when the formField would use a date/time picker
  const datePickerWhitelist = ['datetime', 'date', 'time'];
  const requiresDatePicker = childDataType => datePickerWhitelist.includes(childDataType);

  return (
    <>
      <AgentLookupModal />
      <AutoPopModal
        id="taxAutoPopModal"
        onListingClicked={listingSelected}
        clearForm={clearForm}
        setClearForm={setClearForm}
      />
      <Formik
        innerRef={formRef}
        initialValues={memoizedInitialValues}
        onSubmit={onClickSubmit}
        validate={myValidateForm}
        validateOnBlur={false}
        validateOnChange={false}
      >
        {() => (
          <>
            <OnSubmitValidationError
              onSubmissionError={() => {
                if (sortedErrors?.length > 0) {
                  Swal.fire({
                    allowEnterKey: false,
                    allowEscapeKey: false,
                    allowOutsideClick: false,
                    position: 'center',
                    title: 'Validation Failed',
                    html: `Please correct <strong class="text-danger">${sortedErrors.length}</strong> validation errors and try again.`,
                    confirmButtonText: 'Ok',
                    icon: 'error',
                    iconColor: 'var(--theme-danger)',
                    buttonsStyling: false,
                    customClass: {
                      confirmButton: 'btn btn-danger btn-lg swal-fail-listing-validation',
                    },
                  });
                }
              }}
            />
            <Form onKeyDown={handleEnterKeyDown}>
              <Panel
                customClass={formIsLoading ? 'sticky' : 'd-none'}
                header={getPanelString}
              >
                <FormLoader />
              </Panel>
              <Panel
                customClass={formIsLoading ? 'd-none' : 'sticky'}
                header={getPanelString}
                panelToolbar={(
                  <div>
                    <div style={{ display: 'flex' }} disabled={isFormSubmitting || isFormSavingAsIncomplete || isFormAutoSaving}>
                      {!listingCode?.includes('chg') && ( // TAP will not show on change forms
                        <span data-toggle="modal" data-target="#taxAutoPopModal">
                          <Button
                            name="btnTaxAutopopListing"
                            onClick={() => setClearForm(false)}
                            variant="info btn-sm d-none d-lg-block"
                          >
                            <i className="fas fa-magic mr-2" aria-hidden="true" />
                            Tax Autopop
                          </Button>
                          <Button
                            name="btnTaxAutopopListing"
                            onClick={() => setClearForm(false)}
                            variant="info btn-sm d-block d-lg-none"
                            tooltipText="Tax Autopop"
                          >
                            <i className="fas fa-magic" aria-hidden="true" />
                          </Button>
                        </span>
                      )}
                      {(operationType === 'edit' || operationType === 'create') && (
                        <>
                          <Button
                            name="btnDiscardChangesListing"
                            variant="danger btn-sm d-none d-lg-block"
                            onClick={handleDiscardChanges}
                          >
                            <i className="fas fa-trash-alt mr-2" aria-hidden="true" />
                            Discard Changes
                          </Button>
                          <Button
                            name="btnDiscardChangesListing"
                            variant="danger btn-sm d-block d-lg-none"
                            onClick={handleDiscardChanges}
                            tooltipText="Discard Changes"
                          >
                            <i className="fas fa-trash-alt" aria-hidden="true" />
                          </Button>
                        </>
                      )}
                      {(operationType === 'create' || (standardStatus === 'I' && !listingCode?.includes('chg'))) && ( // decides whether to render button
                        <SaveAsIncomplete
                          onSubmit={onClickSaveIncomplete}
                          buttonText={incompleteButtonText}
                          isDisabled={photosQueued}
                        />
                      )}
                      <Button
                        name="btnSubmitListing"
                        variant="success btn-sm d-none d-lg-block"
                        type="submit"
                        disabled={photosQueued}
                      >
                        {isFormSubmitting
                          ? (
                            <>
                              <span className="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="far fa-chevron-circle-right mr-2" aria-hidden="true" />
                              Submit
                            </>
                          )}
                      </Button>
                      <Button
                        name="btnSubmitListing"
                        variant="success btn-sm d-block d-lg-none"
                        type="submit"
                        tooltipText="Submit"
                      >
                        {isFormSubmitting
                          ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                          : <i className="far fa-chevron-circle-right" aria-hidden="true" />}
                      </Button>
                    </div>
                  </div>
                )}
                footer={orderedSections.length > 1
                  ? (
                    <>
                      <div className="p-3 w-75">
                        <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
                      </div>
                      <div className="pr-2 w-25 d-flex">
                        <Button
                          name="btnPreviousSectionListing"
                          className="btn-sm d-none d-lg-block ml-auto"
                          onClick={handlePreviousClick}
                          disabled={activeSection === 1}
                        >
                          <i className="fas fa-chevron-left mr-2" />
                          Previous
                        </Button>
                        <Button
                          name="btnPreviousSectionListing"
                          className="btn-sm d-block d-lg-none ml-auto"
                          onClick={handlePreviousClick}
                          tooltipText="Previous"
                          disabled={activeSection === 1}
                        >
                          <i className="fas fa-chevron-left" />
                        </Button>
                        <Button
                          name="btnNextSectionListing"
                          className="btn-sm d-none d-lg-block"
                          onClick={handleNextClick}
                          disabled={activeSection === orderedSections.length}
                        >
                          <i className="fas fa-chevron-right mr-2" />
                          Next
                        </Button>
                        <Button
                          name="btnNextSectionListing"
                          className="btn-sm d-block d-lg-none"
                          onClick={handleNextClick}
                          tooltipText="Next"
                          disabled={activeSection === orderedSections.length}
                        >
                          <i className="fas fa-chevron-right" />
                        </Button>
                      </div>
                    </>
                  )
                  : null}
              >
                <Tab.Container
                  id="sections"
                  defaultActiveKey={orderedSections[activeSection - 1]?.sectionName}
                  onSelect={key => {
                    setProgress((1 / orderedSections.length) * 100 * (orderedSections.indexOf(orderedSections.find(x => x.sectionName === key)) + 1));
                    setActiveSection(orderedSections.indexOf(orderedSections.find(x => x.sectionName === key)) + 1);
                  }}
                >
                  <Row>
                    <Col xl={4}>
                      <SectionNav
                        sectionsPerRepeatableSection={sectionsPerRepeatableSection}
                        sections={orderedSections}
                        errPerSection={errPerSection}
                        reqPerSection={reqPerSection}
                      />
                    </Col>
                    <Col xl={8}>
                      <FormSearchBar />
                      <Tab.Content className="pb-6">
                        {pageStructure.map(section => (
                          <Tab.Pane
                            eventKey={section.sectionName.replace(/\s/g, '')}
                            key={section.sectionName.replace(/\s/g, '')}
                          >
                            {section.sectionName !== 'Rooms' ? (
                              <>
                                <h3 className="fs-xl mb-4">{getCustomHeader(section)}</h3>
                                {section.children.map(child => {
                                  if (child.isGroup) {
                                    return (
                                      <LayoutGroupWrapper
                                        key={child.groupName}
                                        validateFigField={validateFigField}
                                        comboBubbleWrap={comboBubbleWrap}
                                        {...child}
                                      />
                                    );
                                  }
                                  if (requiresDatePicker(child.datatype)) {
                                    return (
                                      <FormikField
                                        key={child.RowID}
                                        name={child.FieldName}
                                        validateFigField={validateFigField}
                                        {...child}
                                      />
                                    );
                                  }
                                  return (
                                    <FormikInputGroup
                                      key={child.RowID}
                                      validateFigField={validateFigField}
                                      comboBubbleWrap={comboBubbleWrap}
                                      {...child}
                                    />
                                  );
                                })}
                              </>
                            ) : (
                              <RepeatableRoom validateFigField={validateFigField} roomsPayload={roomsPayload} />
                            )}
                          </Tab.Pane>
                        ))}
                        {/* <Debug /> */}
                        <AutoSave
                          autoSaveEnabled={autoSaveEnabled.current}
                          autoSaveTriggered={handleAutoSave}
                          formValues={formRef?.current?.values}
                          loadingStatus={loadingStatus}
                          listingChanged={listingType !== currListingType}
                        />
                        <FieldValidator
                          validationRules={listingRules}
                          validateFieldName={validateFieldName}
                          validateFlag={validateFlag}
                          validationValue={validationValue}
                          selectedListing={selectedListing}
                          setValidateFlag={setValidateFlag}
                          setValidationValue={setValidationValue}
                          setValidateFieldName={setValidateFieldName}
                          runValidation={fieldsUpdated}
                        />
                        <FormikFieldUpdater
                          agentGroupType={agentGroupType}
                          agentLookup={agentLookup}
                          currCoords={currCoords}
                          definitionType={definitionType}
                          listingFormData={listingFormData}
                          runValidation={fieldsUpdated}
                        />
                      </Tab.Content>
                    </Col>
                  </Row>
                </Tab.Container>
              </Panel>
            </Form>
          </>
        )}
      </Formik>
    </>
  );
};

export default CreateListingsForm;
